/**
 * Admin API Client (Bootstrap Mode)
 *
 * Uses admin bootstrap tokens instead of SSO tokens.
 * Used during initial application setup before SSO is configured.
 * Automatically refreshes expired tokens using the refresh token.
 */

import { createApiClient } from '@shared/services/apiBase';
import { adminAuthService } from '@shared/services/adminAuth';
import type { AppConfig, SSOConfig, User, Platform, PlatformCreate } from '@shared/types';

let isRefreshing = false;
let refreshPromise: Promise<boolean> | null = null;

const client = createApiClient({
  getToken: async () => {
    // If the id token is still valid, use it
    const token = adminAuthService.getIdToken();
    if (token) return token;

    // Token expired — try refreshing
    if (!isRefreshing) {
      isRefreshing = true;
      refreshPromise = adminAuthService.refreshTokens().finally(() => {
        isRefreshing = false;
        refreshPromise = null;
      });
    }

    const refreshed = await (refreshPromise ?? Promise.resolve(false));
    if (refreshed) {
      return adminAuthService.getIdToken();
    }

    // Refresh failed — no valid token
    return null;
  },
  onUnauthorized: () => adminAuthService.logout(),
});

export const adminApiClient = {
  // Configuration
  async getConfig(): Promise<AppConfig> {
    const response = await client.get('/admin/config');
    return response.data;
  },

  async updateConfig(updates: Partial<AppConfig>): Promise<AppConfig> {
    const response = await client.patch('/admin/config', updates);
    return response.data;
  },

  // Platform Configuration
  async getPlatforms(): Promise<Platform[]> {
    const response = await client.get('/admin/platforms');
    return response.data;
  },

  async createPlatform(data: PlatformCreate): Promise<Platform> {
    const response = await client.post('/admin/platforms', data);
    return response.data;
  },

  async updatePlatform(id: string, data: PlatformCreate): Promise<Platform> {
    const response = await client.put(`/admin/platforms/${id}`, data);
    return response.data;
  },

  async deletePlatform(id: string): Promise<void> {
    await client.delete(`/admin/platforms/${id}`);
  },

  // SSO Configuration
  async getSsoStatus(): Promise<{
    active_provider: string | null;
    cognito_entity_id: string;
    cognito_acs_url: string;
    providers: Record<string, { configured: boolean; enabled: boolean; config?: Record<string, string> }>;
  }> {
    const response = await client.get('/admin/sso/status');
    return response.data;
  },

  async getSsoConfigs(): Promise<SSOConfig[]> {
    const response = await client.get('/admin/sso');
    return response.data;
  },

  async configureMsEntra(config: {
    tenantId: string;
    clientId: string;
    clientSecret?: string;
    samlMetadataUrl?: string;
  }): Promise<{ success: boolean; message: string }> {
    const payload = {
      tenant_id: config.tenantId,
      client_id: config.clientId,
      client_secret: config.clientSecret,
      saml_metadata_url: config.samlMetadataUrl,
    };
    const response = await client.put('/admin/sso/microsoft-entra', payload);
    return response.data;
  },

  async configureGoogleWorkspace(config: {
    clientId?: string;
    clientSecret?: string;
    domain: string;
    samlMetadataUrl?: string;
    serviceAccountJson?: string;
    adminEmail?: string;
  }): Promise<{ success: boolean; message: string }> {
    const payload = {
      domain: config.domain,
      saml_metadata_url: config.samlMetadataUrl,
      service_account_json: config.serviceAccountJson,
      admin_email: config.adminEmail,
    };
    const response = await client.put('/admin/sso/google-workspace', payload);
    return response.data;
  },

  async activateSso(provider: string): Promise<{ success: boolean; message: string }> {
    const response = await client.post('/admin/sso/activate', { provider });
    return response.data;
  },

  async deactivateSso(): Promise<{ success: boolean; message: string }> {
    const response = await client.post('/admin/sso/deactivate');
    return response.data;
  },

  async fetchEntraGroups(config: {
    tenantId: string;
    clientId: string;
    clientSecret: string;
  }): Promise<{ groups: Array<{ id: string; name: string; description: string | null }> }> {
    const payload = {
      tenant_id: config.tenantId,
      client_id: config.clientId,
      client_secret: config.clientSecret,
    };
    const response = await client.post('/admin/sso/microsoft-entra/groups', payload);
    return response.data;
  },

  async fetchGoogleGroups(config: {
    serviceAccountJson: string;
    domain: string;
    adminEmail: string;
  }): Promise<{ groups: Array<{ id: string; name: string; email?: string; description: string | null }> }> {
    const payload = {
      service_account_json: config.serviceAccountJson,
      domain: config.domain,
      admin_email: config.adminEmail,
    };
    const response = await client.post('/admin/sso/google-workspace/groups', payload);
    return response.data;
  },

  async getRoleMappings(provider: string): Promise<{
    admin_group_ids: string[];
    employee_group_ids: string[];
    leadership_group_ids: string[];
    cached_groups: { id: string; name: string; description: string | null }[];
  }> {
    const response = await client.get(`/admin/sso/${provider}/role-mappings`);
    return response.data;
  },

  async updateRoleMappings(
    provider: string,
    mappings: {
      admin_group_ids: string[];
      employee_group_ids: string[];
      leadership_group_ids: string[];
      cached_groups?: { id: string; name: string; description: string | null }[];
    }
  ): Promise<{ success: boolean; message: string }> {
    const response = await client.put(`/admin/sso/${provider}/role-mappings`, mappings);
    return response.data;
  },

  // User Management
  async getUsers(): Promise<User[]> {
    const response = await client.get('/users');
    return response.data;
  },

  async syncUsers(): Promise<{ added: number; updated: number; deactivated: number }> {
    const response = await client.post('/admin/users/sync');
    return response.data;
  },

  async toggleDownloadAccess(userId: string, downloadAllowed: boolean): Promise<User> {
    const response = await client.patch(`/admin/users/${userId}/download-access`, { download_allowed: downloadAllowed });
    return response.data;
  },

  async toggleDeleteProjectsAccess(userId: string, deleteProjectsAllowed: boolean): Promise<User> {
    const response = await client.patch(`/admin/users/${userId}/delete-projects-access`, { delete_projects_allowed: deleteProjectsAllowed });
    return response.data;
  },
};
